import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useReturnTo } from '../common/useReturnTo';
import { Trans, useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronDown, faCheck, faSearch, faNoteSticky } from '@fortawesome/free-solid-svg-icons';

import RouteHeader from "../common/RouteHeader";
import PageHeaderKebab from "../common/PageHeaderKebab";
import { getNormalizedString } from "../../helpers/commons";
import "./PanelHeader.css";

/**
 * Fused header for the class-level Panel. Renders inside MainPage's top bar (via
 * setPageHeader) so we don't ship a second "Painel" line.
 *
 * Modes (mock: painel-header-fundido.html):
 *  · 0 turmas → h1 estático "Selecione uma turma".
 *  · 1 turma  → h1 estático com o nome (recorte A). Sem chevron, sem dropdown.
 *  · N > 1    → h1 é o próprio seletor: nome da turma + chevron, click abre dropdown
 *               (recortes B/C). Threshold ~8 ativas: dropdown ganha search field (D).
 *               Encerradas (active=false) escondidas atrás de "Ver turmas encerradas · N".
 */
const SEARCH_THRESHOLD = 8;

const PanelHeader = ({ schoolName, classes, selectedClassId, onClassChange, noteCount, onOpenNotes }) => {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [showArchived, setShowArchived] = useState(false);
	const wrapRef = useRef(null);

	// Split active vs archived. `active` do backend vem como String ("true"/"false"); trato ambos.
	const isActive = (c) => c.active === true || c.active === "true" || c.active == null;
	const activeClasses = (classes || []).filter(isActive);
	const archivedClasses = (classes || []).filter(c => !isActive(c));
	const showSearch = activeClasses.length > SEARCH_THRESHOLD;

	const selected = classes?.find(c => c.id === selectedClassId) || null;
	const isMulti = activeClasses.length > 1;

	// Close dropdown on outside click.
	useEffect(() => {
		if (!open) return;
		const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
		document.addEventListener('mousedown', onDown);
		return () => document.removeEventListener('mousedown', onDown);
	}, [open]);

	// Reset query + archived toggle when closing.
	useEffect(() => { if (!open) { setQuery(""); setShowArchived(false); } }, [open]);

	const filterByQuery = (list) => {
		if (!query.trim()) return list;
		const q = getNormalizedString(query);
		return list.filter(c => getNormalizedString(c.name || "").includes(q));
	};

	const handlePick = (id) => { onClassChange(id); setOpen(false); };

	// Enquanto `classes` é null, o header do shell ainda não sabe o nome da
	// turma — antes ficava com string vazia ("débito temporário loading state").
	// Agora mostra shimmer no lugar do título pra alinhar com o skeleton do
	// body (Fatia C do loading system). "Selecione uma turma" só aparece em
	// REAL empty state (lista=[]).
	const loadingClasses = classes == null;
	const title = selected?.name || (loadingClasses ? '' : t("panel.class.pick-class"));

	// School crumb first — só multi-scope (parent decide via prop). Painéis são
	// school-scoped; sem esse crumb, multi-scope users perdem o "em qual escola
	// estou" (setPageHeader sobrescreve o RouteHeader default do MainPage).
	/* ⚠️ `null` no fallback: sem origem NÃO há voltar. Quem chegou pela sidebar
	   está na raiz da seção, e seta que volta para lugar nenhum é pior que seta
	   nenhuma. Ver `useReturnTo`. */
	const voltarPara = useReturnTo(null);

	const crumbs = [
		/* 🔴 ESCOLA ou PAINEL, nunca os dois (PO, 2026-08-28). Os dois ocupam o
		   MESMO slot de contexto no L1, e cada um serve a um usuário:
		     · multi-escola / root → o nome da escola responde "em qual estou"
		     · escola única (Rômulo, Wallace) → o nome é ruído, e "Painel"
		       devolve o contexto do módulo que o crumb de módulo não dá
		   `schoolName` já é null exatamente quando há uma escola só — a
		   condição não precisou ser inventada, só lida.
		   ⚠️ No mobile o "Painel" some junto (`pnl-crumb-hide-mobile`): lá o
		   espaço é o recurso escasso e o módulo à direita já situa. */
		...(schoolName
			? [{ label: schoolName },
			   { label: <Trans i18nKey="header.navigation-titles.panel">Painel</Trans>, className: 'pnl-crumb-hide-mobile' }]
			: [{ label: <Trans i18nKey="header.navigation-titles.panel">Painel</Trans> }]),
		{ label: <Trans i18nKey="panel.class.crumb-classes">Turmas</Trans> },
	];

	const titleNode = loadingClasses ? (
		<span className="sk" style={{ width: 180, height: 22, borderRadius: 4, display: 'inline-block' }} aria-hidden="true" />
	) : isMulti ? (
		<button
			type="button"
			className={"pnl-header-h1sel" + (open ? " pnl-header-h1sel-open" : "")}
			onClick={() => setOpen(!open)}
		>
			<span className="pnl-header-h1sel-txt">{title}</span>
			<FontAwesomeIcon icon={faChevronDown} className="pnl-header-cv" />
		</button>
	) : (
		<div className="pnl-header-h1static">{title}</div>
	);

	return (
		<div className="pnl-header">
			{/* 🔴 MESMA peça do `PanelStudentHeader`: `back-btn` IRMÃO do titlewrap,
			    dentro do `pnl-header`. Cheguei a inventar uma seta e um CSS novos
			    dentro do `RouteHeader`; o portal já tinha a sua. */}
			{voltarPara && (
				<Link to={voltarPara} className="back-btn" aria-label={t('panel.student.back')}>
					<FontAwesomeIcon icon={faChevronLeft} />
				</Link>
			)}
			<div className="pnl-header-titlewrap" ref={wrapRef}>
				<RouteHeader crumbs={crumbs} title={titleNode} />

				{open && isMulti && (
					<div className="pnl-header-dd">
						{showSearch && (
							<div className="pnl-header-ddsearch">
								<FontAwesomeIcon icon={faSearch} />
								<input
									autoFocus
									value={query}
									onChange={e => setQuery(e.target.value)}
									placeholder={t("panel.class.dropdown.search-placeholder")}
								/>
							</div>
						)}
						{filterByQuery(activeClasses).map(c => (
							<div
								key={c.id}
								className={"pnl-header-ddi" + (c.id === selectedClassId ? " pnl-header-ddi-on" : "")}
								onClick={() => handlePick(c.id)}
							>
								{c.name}
								{c.id === selectedClassId && (
									<FontAwesomeIcon icon={faCheck} className="pnl-header-ddi-ck" />
								)}
							</div>
						))}
						{filterByQuery(activeClasses).length === 0 && (
							<div className="pnl-header-ddempty">
								<Trans i18nKey="panel.class.dropdown.no-match">No matching classes</Trans>
							</div>
						)}
						{archivedClasses.length > 0 && !showArchived && (
							<div className="pnl-header-ddsec">
								<div
									className="pnl-header-ddi pnl-header-ddi-alt"
									onClick={() => setShowArchived(true)}
								>
									<Trans i18nKey="panel.class.dropdown.show-archived">Show closed classes</Trans>
									<span className="pnl-header-ddi-count">{archivedClasses.length}</span>
								</div>
							</div>
						)}
						{showArchived && archivedClasses.length > 0 && (
							<div className="pnl-header-ddsec">
								<div className="pnl-header-ddgroup">
									<Trans i18nKey="panel.class.dropdown.archived-label">Closed classes</Trans>
								</div>
								{filterByQuery(archivedClasses).map(c => (
									<div
										key={c.id}
										className={"pnl-header-ddi pnl-header-ddi-archived" + (c.id === selectedClassId ? " pnl-header-ddi-on" : "")}
										onClick={() => handlePick(c.id)}
									>
										{c.name}
										{c.id === selectedClassId && (
											<FontAwesomeIcon icon={faCheck} className="pnl-header-ddi-ck" />
										)}
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>

			{/* 🔴 KEBAB, não botão (PO, 2026-09-09): é o padrão que o portal já usa
			    nas telas de Admin, ocupa menos espaço, e reusa o `PageHeaderKebab`
			    com o mesmo contrato de ação do `DataTable`.
			    ⚠️ O ITEM MUDA COM O ESTADO (§3.2): com anotação ele diz `Ver
			    anotações · 3` — a contagem é o que responde se vale abrir; sem
			    nenhuma, `Escrever anotação`, e o modal abre já no editor. Mesmo
			    destino; o rótulo é que não promete lista vazia. */}
			{onOpenNotes && (
				<div className="pnl-header-kb">
					<PageHeaderKebab actions={[{
						handler: onOpenNotes,
						/* 🔴 `Ver TODAS as anotações` (PO, 2026-09-10). "da turma" tem
						   leitura ambígua — pode passar por "as de nível turma, não as
						   dos alunos", que é o oposto do que este item faz. E é aqui que
						   a ambiguidade custa: quem vem do evento vê `· 2` e aqui vê
						   `· 5`, e o rótulo é a única coisa que explica a diferença.
						   "de quê" já é respondido pelo crumb do modal. */
						label: noteCount > 0
							? t('panel.notes.kebab-see', { count: noteCount })
							: t('panel.notes.kebab-write'),
						icon: faNoteSticky,
					}]} />
				</div>
			)}
		</div>
	);
};

export default PanelHeader;
